import axios from 'axios';
import store from '../store/index.js';
import router from '@/router/index.js';
import { Loading } from 'element-ui';
import { MesssageBoxQuestion, MessageError } from '@/components/util/MessageBox';
import { isPlatformApi, withPlatformParams } from '@/constants/platform';

let loadingInstance; // 加载实例
let activeRequests = 0; // 活跃请求计数器（关键变量）

// 白名单：不显示加载动画的接口
const noLoadingUrls = [
  '/order/toFindOrderDetail.do',
  '/class/toCode.html',
  '/user/updateDBTrack.do'
];

// 显示加载动画（仅当第一次有活跃请求时）
const showLoading = () => {
  if (!loadingInstance) {
    loadingInstance = Loading.service({
      lock: true,
      text: '加载中...',
      background: 'rgba(0, 0, 0, 0.7)'
    });
  }
};

// 关闭加载动画（仅当最后一个活跃请求结束时）
const closeLoading = () => {
  if (loadingInstance) {
    loadingInstance.close();
    loadingInstance = null;
  }
};

// 创建Axios实例
const http = axios.create({
  headers: {
    'Content-Type': 'application/x-www-form-urlencoded',
    'equipment': '02'
  },
  baseURL: process.env.VUE_APP_BASE_API,
  timeout: 100000,
});

// 存储正在进行的请求，阻止重复提交
const pendingRequests = new Map();

// 请求拦截器
http.interceptors.request.use(config => {
  // 判断是否需要显示加载动画
  const shouldShowLoading = !noLoadingUrls.some(url => 
    config.url.startsWith(url)
  );
  // 将是否需要加载的标记存入config，供响应时使用
  config.shouldShowLoading = shouldShowLoading;

  // 处理加载动画计数
  if (shouldShowLoading) {
    activeRequests++; // 活跃请求+1
    if (activeRequests === 1) { // 第一个需要加载的请求
      showLoading();
    }
  }

  // 重复提交校验
  const requestKey = `${config.method}-${config.url}`;
  if (requestKey.indexOf('#') !== -1 && pendingRequests.has(requestKey)) {
    // 若重复请求，需要减少计数（如果是需要加载的请求）
    if (shouldShowLoading) {
      activeRequests--;
    }
    return Promise.reject(new Error('请勿重复提交:' + config.url));
  }
  pendingRequests.set(requestKey, config);

  // 取消请求配置
  config.cancelToken = new axios.CancelToken(cancel => {
    config.__requestCancel = cancel;
  });

  // 添加token和uuid（优先从浏览器缓存读取token）
  const token = localStorage.getItem('nxzj_token') || store.state.user.token;
  if (token) {
    config.headers['authorization'] = token;
  }
  const uuid = store.state.user.uuid;
  if (uuid) {
    config.headers['uuid'] = uuid;
  }

  // 添加时间戳
  const timestampInSeconds = Math.floor(Date.now() / 1000);
  if (config.method === 'post') {
    config.data = config.data || {};
    config.data.timestamp = timestampInSeconds;
  } else {
    config.params = config.params || {};
    config.params.timestamp = timestampInSeconds;
  }

  // 平台条件查询：与 nxzj-api 中 platformId 参数对齐
  if (isPlatformApi(config.url)) {
    if (config.method === 'post' || config.method === 'POST') {
      const payload = config.data && typeof config.data === 'object' && !(config.data instanceof FormData)
        ? config.data
        : {};
      config.data = withPlatformParams(payload);
    } else {
      config.params = withPlatformParams(config.params || {});
    }
  }

  return config;
}, error => {
  // 请求拦截器错误处理（如参数错误）
  if (error.config?.shouldShowLoading) {
    activeRequests--; // 减少计数
    if (activeRequests === 0) { // 最后一个请求结束
      closeLoading();
    }
  }
  return Promise.reject(error);
});

// 响应拦截器
let hasShownLoginPrompt = false;
http.interceptors.response.use(
  response => {
    const requestKey = `${response.config.method}-${response.config.url}`;
    pendingRequests.delete(requestKey);

    // 处理加载计数
    if (response.config.shouldShowLoading) {
      activeRequests--; // 活跃请求-1
      if (activeRequests === 0) { // 所有请求都结束了
        closeLoading();
      }
    }

    // 未登录处理
    if (response.data?.result === '-1') {
      store.commit('SET_TOKEN', '');
      // 清除浏览器缓存中的token
      localStorage.removeItem('nxzj_token');
      store.commit('SET_USERID', '');
      store.commit('SET_AVATAR', '');
      store.commit('SET_NAME', '');
      
      if (!hasShownLoginPrompt) {
        hasShownLoginPrompt = true;
        MesssageBoxQuestion('您还没有登录，请先去登录').then(() => {
          hasShownLoginPrompt = false;
          router.push({ path: '/login' }).catch(() => {});
        }).catch(() => {
          hasShownLoginPrompt = false;
        });
      }
    }

    return response.data;
  },
  error => {
    // 错误处理（包括超时、取消请求等）
    if (error.config?.shouldShowLoading) {
      activeRequests--; // 减少计数
      if (activeRequests === 0) { // 所有请求都结束了
        closeLoading();
      }
    }

    // 超时错误处理
    if (axios.isCancel(error)) {
      if (error.code === 'ECONNABORTED') {
        MessageError('请求超时，请稍后重试');
      }
    } else {
      console.log('请求错误', error);
    }

    return Promise.reject(error);
  }
);

export default http;
